import React from 'react';
import { Calendar, MapPin, Award } from 'lucide-react';
import { mockCities } from '../mock';

export const ClosingGapCampaignSection = () => {
  const timeline = [
    {
      icon: Calendar,
      date: 'March 8',
      title: 'IWD Global Launch',
      cities: 'Montreal · New York · London · Dubai · Cape Town'
    },
    {
      icon: Calendar,
      date: 'March–November',
      title: 'Monthly Activations',
      cities: '25+ countries: redistribution events, Secret Letter drops, WEPs outreach, grassroots storytelling. Every 30 days.'
    },
    {
      icon: Award,
      date: '',
      title: 'Global Gala & Solidarity Walk',
      cities: 'Montreal: pitch competitions, impact awards, 5% redistribution to local shelters, announcement of next host city'
    }
  ];

  const goals = [
    { number: '25', label: 'Countries Activated' },
    { number: '150+', label: 'Monthly Redistribution Events' },
    { number: '15,000', label: 'Secret Letters Distributed' },
    { number: '100', label: 'New WEPs Business Signatories' },
    { number: '950–1,200', label: 'Women/Families Supported via Redistribution' },
    { number: '850–1,200', label: 'Entrepreneurs Funded Through Incubators' }
  ];

  const allCities = [
    ...mockCities.phase1,
    ...mockCities.phase2,
    ...mockCities.phase3,
    ...mockCities.phase4
  ];

  return (
    <section id="closing-gap-campaign" className="py-20" style={{ backgroundColor: 'var(--white-soft)' }}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="lhsc-heading text-4xl md:text-5xl mb-6">
            WE ARE NOT WAITING FOR PERMISSION.
          </h2>
          <blockquote className="lhsc-pull-quote text-2xl md:text-3xl max-w-4xl mx-auto">
            "The campaign moves forward — because our model is community-funded, 
            locally led, and built to scale with integrity."
          </blockquote>
        </div>

        {/* Campaign Overview */}
        <div className="lhsc-body max-w-4xl mx-auto mb-12 space-y-4">
          <p>
            Closing the Gap is a global women-led campaign delivering humanitarian aid and economic empowerment in 25+ countries. 
            Its same-day redistribution model eliminates warehouses, waiting periods, and bureaucracy. Women are not just 
            beneficiaries — they become active participants, fostering agency and self-reliance.
          </p>
        </div>

        {/* Timeline */}
        <div className="mb-12">
          <div className="relative">
            {/* Connecting Line - Desktop */}
            <div className="hidden md:block absolute left-0 right-0 top-16 h-1" style={{ backgroundColor: 'var(--mauve-rose)' }}></div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
              {timeline.map((item, index) => (
                <div key={index} className="text-center">
                  <div 
                    className="w-32 h-32 rounded-full mx-auto mb-6 flex items-center justify-center bg-white border-4"
                    style={{ borderColor: 'var(--mauve-rose)' }}
                  >
                    <item.icon size={48} style={{ color: 'var(--mauve-rose)' }} />
                  </div>
                  <div className="bg-white p-6 border border-[var(--pink-dusty)]">
                    {item.date && (
                      <div className="lhsc-heading text-sm mb-2" style={{ color: 'var(--mauve-rose)' }}>
                        {item.date}
                      </div>
                    )}
                    <h3 className="lhsc-heading text-lg mb-3">{item.title}</h3>
                    <p className="lhsc-body text-sm">{item.cities}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* City Rollout */}
        <div className="mb-12">
          <h3 className="lhsc-heading text-2xl text-center mb-8">City Rollout</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { phase: 'Phase 1', cities: mockCities.phase1 },
              { phase: 'Phase 2', cities: mockCities.phase2 },
              { phase: 'Phase 3', cities: mockCities.phase3 },
              { phase: 'Phase 4', cities: mockCities.phase4 }
            ].map((phaseData, index) => (
              <div
                key={index}
                className="p-6 border border-[var(--pink-dusty)] bg-white"
                style={{ borderTopWidth: '4px', borderTopColor: 'var(--blue-periwinkle)' }}
              >
                <h4 className="lhsc-heading text-base mb-4" style={{ color: 'var(--mauve-rose)' }}>
                  {phaseData.phase}
                </h4>
                <ul className="lhsc-body text-sm space-y-2">
                  {phaseData.cities.map((city, idx) => (
                    <li key={idx} className="flex items-start">
                      <MapPin size={16} className="mr-2 flex-shrink-0 mt-1" style={{ color: 'var(--slate-grey)' }} />
                      {city}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <p className="lhsc-body text-center mt-6 italic">
            "This is not copy-paste expansion. Every city adapts to local culture, language, 
            UN presence, and gender-equity priorities."
          </p>
        </div>

        {/* Year 1 Goals */}
        <div className="mb-12">
          <h3 className="lhsc-heading text-2xl text-center mb-8">Year 1 Goals</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {goals.map((goal, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl lhsc-heading mb-2" style={{ color: 'var(--mauve-rose)' }}>
                  {goal.number}
                </div>
                <p className="lhsc-body text-xs">{goal.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://docs.google.com/forms/d/e/1FAIpQLSfZCS6ey74o1BCchyhOOTjI5MlK1O_4gem1nPS69HLROzBbQA/viewform"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 rounded-md text-white font-semibold text-sm tracking-wider"
            style={{ 
              backgroundColor: 'var(--mauve-rose)',
              fontFamily: 'Josefin Sans, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontWeight: 500
            }}
          >
            Volunteer in Your City
          </a>
          <a
            href="https://entrepreneurladyhustler.com/en/closing-the-gap-womens-humanitarian-campaign/"
            target="_blank"
            rel="noopener noreferrer"
            className="px-8 py-4 rounded-md font-semibold text-sm tracking-wider border-2"
            style={{ 
              color: 'var(--text-dark)', 
              borderColor: 'var(--mauve-rose)',
              backgroundColor: 'transparent',
              fontFamily: 'Josefin Sans, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontWeight: 500
            }}
          >
            Learn More About Closing the Gap
          </a>
        </div>
      </div>
    </section>
  );
};
